import { useState } from 'react';
import { X, FileText, ArrowRight } from 'lucide-react';

interface TranscriptInputProps {
  meetingInfo: { company_name: string; dm_full_name: string; meeting_date: string };
  onSubmit: (transcript: string) => void;
  onSkip: () => void;
  onClose: () => void;
}

export function TranscriptInput({ meetingInfo, onSubmit, onSkip, onClose }: TranscriptInputProps) {
  const [transcript, setTranscript] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div>
            <h3 className="text-lg font-extrabold text-gray-900 font-display">Meeting Transcript</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {meetingInfo.company_name} - {meetingInfo.dm_full_name} ({meetingInfo.meeting_date})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-start gap-3 mb-4 p-3 bg-[#1875FF]/5 rounded-xl border border-[#1875FF]/10">
            <FileText size={18} className="text-[#1875FF] mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700">
              Paste your meeting transcript below for a detailed analysis. If you don't have a transcript, you can skip this step and I'll still provide feedback based on available conversation history.
            </p>
          </div>

          <textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            placeholder="Paste the meeting transcript here..."
            className="w-full h-64 p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#1875FF] focus:ring-2 focus:ring-[#1875FF]/20 transition-all text-sm resize-none"
            autoFocus
          />

          <div className="flex items-center justify-between mt-4">
            <button
              onClick={onSkip}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
            >
              Skip - analyze without transcript
            </button>
            <button
              onClick={() => onSubmit(transcript)}
              disabled={!transcript.trim()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-xl font-semibold text-sm hover:shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Analyze Meeting
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
